import React from 'react';
import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { userTypes } from '../../util/userTypes';
import { CollapseFAQ, H1, H2, LayoutSingleColumn, NamedLink, Page } from '../../components';
import SectionFooter from '../PageBuilder/SectionBuilder/SectionFooter';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import TrustWidgetSmall from '../../components/StaticPageContainers/TrustWidgetSmall';

import ContinueIcon from '../TopbarContainer/Topbar/images/continue-icon.svg';
import TrustPyramidImage from './images/trust-pyramid.jpg';
import IdVerificationImage from './images/id-verification.jpg';
import MeetingSitterImage from './images/meeting-sitter.jpg';
import PetCameraImage from './images/pet-camera.jpg';

import css from './TrustAndSafetyPage.module.css';

import { faqs } from './faqs';

const TrustAndSafetyPage = () => {
    const config = useConfiguration();
    const intl = useIntl();

    const marketplaceName = config.marketplaceName;
    const schemaTitle = intl.formatMessage({ id: 'TrustAndSafetyPage.schemaTitle' });
    const schemaDescription = intl.formatMessage({ id: 'TrustAndSafetyPage.schemaDescription' }, { marketplaceName });
    return (
        <Page
            title={schemaTitle}
            description={schemaDescription}
        >
            <LayoutSingleColumn
                topbar={<TopbarContainer />}
                footer={<SectionFooter />}
                mainColumnClassName={css.mainColumnClassName}
            >
                <div className={css.root}>
                    <H1 className={css.mainTitle}>
                        Trust &amp; Safety at HouseSit Match
                    </H1>
                    <p className={css.heroLead}>
                        Trust isn’t created by a badge.
                    </p>
                    <p className={css.heroSub}>
                        It’s built through transparent processes, verified identities, honest reviews and real people who are there when you need them.
                    </p>
                    <p className={css.info}>
                        At HouseSit Match we’ve spent more than a decade building a trusted house sitting platform where homeowners and house sitters can connect with confidence. Those layers of trust and safety matter because homeowners aren’t simply sharing a property—they’re entrusting someone with their home, their pets and their peace of mind. Everything we do starts with trust to build peace of mind.
                    </p>

                    <p className={css.subtitle}>
                        Trusted by homeowners and house sitters worldwide
                    </p>
                    <TrustWidgetSmall />
                    <div className={css.badgeRow}>
                        <div className={css.badgeItem}>🛡️ Triple Verification</div>
                        <div className={css.badgeItem}>👩 Real Human Support</div>
                        <div className={css.badgeItem}>🐾 Pet Emergency Guidance</div>
                        <div className={css.badgeItem}>🏠 Family-run</div>
                        <div className={css.badgeItem}>🌍 International Community</div>
                    </div>
                    <p className={css.info}>
                        Because trust isn’t just one feature or a name—it’s built layer by layer over many member experiences.
                    </p>

                    <div className={css.sectionImageWrap}>
                        <img
                            src={IdVerificationImage}
                            alt="A member handing over a passport and driving licence during HouseSit Match's identity verification process."
                            className={css.sectionImage}
                        />
                    </div>

                    <H2 className={css.sectionTitle}>
                        Our 7 Layers of Trust at a Glance
                    </H2>
                    <ol className={css.layersList}>
                        <li>Trust and Safety Pyramid</li>
                        <li>Founder Promise</li>
                        <li>Safety Promise</li>
                        <li>Real Member Stories</li>
                        <li>Trust and Safety Scorecard</li>
                        <li>The Numbers</li>
                        <li>Further Reading</li>
                    </ol>
                    <p className={css.info}>
                        Every layer supports the next. Remove one, and trust becomes weaker. Together, they create complete peace of mind.
                    </p>

                    <div className={css.imageContainer}>
                        <img
                            src={TrustPyramidImage}
                            alt="The HSM Trust Pyramid: seven layers of trust—identity verification, pet emergency support, secure communication, background checks, reviews and references, human support, and peace of mind."
                            className={css.pyramidImage}
                        />
                    </div>

                    <H2 className={css.sectionTitle}>
                        A Founder’s Promise
                    </H2>
                    <div className={css.quoteCard}>
                        <p className={css.quoteLead}>
                            “I founded HouseSit Match because trust and safety should never be left to chance.”
                        </p>
                        <p className={css.info}>
                            As both a homeowner and an experienced house sitter myself, I understand exactly what it feels like to hand over your keys—or to be entrusted with someone else’s home and beloved pets.
                        </p>
                        <p className={css.info}>
                            That’s why we’ve built HouseSit Match around careful verification, honest communication and real human support.
                        </p>
                        <p className={css.info}>
                            We don’t just hope everything goes well—we work hard behind the scenes to help make it so. Every new member represents someone’s home, someone’s pets and someone’s trust. We never lose sight of that responsibility.
                        </p>
                        <p className={css.info}>
                            If you ever need us, we’re only a phone call or email away.
                        </p>
                        <p className={css.quoteAuthor}>
                            — Lamia Walker, Founder
                        </p>
                    </div>

                    <H2 className={css.sectionTitle}>
                        Our HSM Safety Promise
                    </H2>
                    <p className={css.info}>
                        We will never compromise member safety for growth. We work to ensure safe house sitting.
                    </p>
                    <p className={css.info}>
                        If something doesn’t feel right to you, tell us:
                    </p>
                    <ul className={css.checklist}>
                        <li>✓ We listen.</li>
                        <li>✓ We investigate.</li>
                        <li>✓ We support both parties fairly.</li>
                        <li>✓ We believe transparency builds trust.</li>
                    </ul>

                    <div className={css.sectionImageWrap}>
                        <img
                            src={PetCameraImage}
                            alt="A cat sitting beside a smart pet camera and feeder, part of HouseSit Match's pet emergency guidance and smart home safety approach."
                            className={css.sectionImage}
                        />
                    </div>

                    <H2 className={css.sectionTitle}>
                        Real Member Safety Stories
                    </H2>
                    <div className={css.sectionImageWrap}>
                        <img
                            src={MeetingSitterImage}
                            alt="A homeowner introducing their dog to a house sitter in person."
                            className={css.sectionImage}
                        />
                    </div>
                    <div className={css.testimonialsGrid}>
                        <div className={css.card}>
                            <p className={css.info}>
                                “I was nervous inviting a stranger into my home, but the ID checks, police verification and support team gave me confidence. Then being able to meet with the sitter in a virtual call on the site before revealing any other contact details gave me real confidence. It all worked out well. Good process!”
                            </p>
                            <p className={css.reviewer}>
                                — Jan, Homeowner in Chesham, UK
                            </p>
                        </div>
                        <div className={css.card}>
                            <p className={css.info}>
                                “There is no other house sitting platform that asks homeowners and house sitters to go through the same checks. As a house sitter who travels around the world by house and pet sitting, I find this protocol reassuring and it gives me more confidence.”
                            </p>
                            <p className={css.reviewer}>
                                — Paulette, House Sitter from Montreal, Canada
                            </p>
                        </div>
                        <div className={css.card}>
                            <p className={css.info}>
                                “When I had a question before the sit, a real person answered. It was very reassuring. And they checked in again just before the sitter arrived to make sure I had what I needed. Such a relief to know there are humans in the business helping us.”
                            </p>
                            <p className={css.reviewer}>
                                — Teresa, Homeowner in Alicante, Spain
                            </p>
                        </div>
                    </div>

                    <div className={css.ctaBox}>
                        <p className={css.ctaBoxTitle}>
                            Ready to find a sitter you can trust?
                        </p>
                        <p className={css.info}>
                            Browse verified profiles, watch video intros, and read honest reviews before you decide — all the reassurance, none of the guesswork.
                        </p>
                        <NamedLink
                            className={css.ctaButton}
                            name="MembershipPlansPage"
                            params={{ accountType: userTypes.homeowner }}
                        >
                            Join as a Homeowner to Find a Verified House Sitter
                            <img src={ContinueIcon} className={css.icon} alt="" />
                        </NamedLink>
                    </div>

                    <div className={css.testimonialsGrid}>
                        <div className={css.card}>
                            <p className={css.info}>
                                “We were going away for a long time, six weeks in total, and we weren’t sure how to go about finding sitters we could rely on. As soon as I could see the profiles, and know that checks had been done, and I could interview people first, I felt this was a good and trusted solution for us. It all worked out well, the sitters were great, our pets were happy and we had a great trip with peace of mind knowing due diligence had been done by HouseSit Match.”
                            </p>
                            <p className={css.reviewer}>
                                — Peter, Homeowner
                            </p>
                        </div>
                        <div className={css.card}>
                            <p className={css.info}>
                                “As an older, solo female sitter, the triple vetting done for homeowners is my way of being assured that I can fully relax and be confident in choosing a sit, knowing that I will be safe in taking even an uber-rural sit anywhere, alone. I can be completely at ease. And homeowners can be confident in their choice of me with not only their home and possessions but as the guardian of their loved pets.”
                            </p>
                            <p className={css.reviewer}>
                                — Maria, House Sitter from Italy
                            </p>
                        </div>
                    </div>

                    <H2 className={css.sectionTitle}>
                        Our Trust Checklist
                    </H2>
                    <div className={css.tableWrapper}>
                        <table className={css.table}>
                            <thead>
                                <tr>
                                    <th className={css.th}>Trust Feature</th>
                                    <th className={css.th}>HouseSit Match</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className={css.td}>Identity verification (x3 factors)</td>
                                    <td className={css.td}>🛡️✅⭐</td>
                                </tr>
                                <tr>
                                    <td className={css.td}>Optional police checks</td>
                                    <td className={css.td}>🛡️✅⭐</td>
                                </tr>
                                <tr>
                                    <td className={css.td}>Transparent reviews</td>
                                    <td className={css.td}>🛡️✅⭐</td>
                                </tr>
                                <tr>
                                    <td className={css.td}>Human support</td>
                                    <td className={css.td}>🛡️✅⭐</td>
                                </tr>
                                <tr>
                                    <td className={css.td}>Pet emergency guidance</td>
                                    <td className={css.td}>🛡️✅⭐</td>
                                </tr>
                                <tr>
                                    <td className={css.td}>Family-run</td>
                                    <td className={css.td}>🛡️✅⭐</td>
                                </tr>
                                <tr>
                                    <td className={css.td}>Founder &amp; Team Accessible</td>
                                    <td className={css.td}>🛡️✅⭐</td>
                                </tr>
                                <tr>
                                    <td className={css.td}>4.9/5 Trustpilot Excellent Rating</td>
                                    <td className={css.td}>🛡️✅⭐</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p className={css.info}>
                        Every trust feature has been designed to make HouseSit Match a secure house sitting platform where both homeowners and house sitters can arrange sits with confidence.
                    </p>

                    <H2 className={css.sectionTitle}>
                        Why HouseSit Match is Different
                    </H2>
                    <p className={css.info}>
                        Some online marketplaces are designed to let members manage everything themselves.
                    </p>
                    <p className={css.info}>
                        HouseSit Match is unique; it combines technology with real human support because trust is too important to automate completely. We believe trust is too important to automate completely. Technology helps us verify identities, checking for any history of fraud, and manage our platform efficiently, but people build relationships.
                    </p>
                    <p className={css.info}>
                        That’s why our members know they can always reach a real person when they need advice, reassurance or support.
                    </p>

                    <H2 className={css.sectionTitle}>
                        Our Numbers Tell the Story
                    </H2>
                    <div className={css.statsGrid}>
                        <div className={css.statCard}>
                            <p className={css.statValue}>10+ Years</p>
                            <p className={css.statLabel}>Matching homeowners and sitters worldwide</p>
                        </div>
                        <div className={css.statCard}>
                            <p className={css.statValue}>⭐ 4.9</p>
                            <p className={css.statLabel}>Trustpilot rating</p>
                        </div>
                        <div className={css.statCard}>
                            <p className={css.statValue}>🌍 25+</p>
                            <p className={css.statLabel}>Countries represented</p>
                        </div>
                        <div className={css.statCard}>
                            <p className={css.statValue}>❤️ Family-run</p>
                            <p className={css.statLabel}>A trusted community</p>
                        </div>
                    </div>

                    <div className={css.ctaBox}>
                        <p className={css.ctaBoxTitle}>
                            Want to join a platform that has your back too?
                        </p>
                        <p className={css.info}>
                            Verified homeowners, real support if anything comes up, and a community that’s been doing this for over a decade.
                        </p>
                        <NamedLink
                            className={css.ctaButton}
                            name="MembershipPlansPage"
                            params={{ accountType: userTypes.housesitter }}
                        >
                            Apply to Become a House Sitter
                            <img src={ContinueIcon} className={css.icon} alt="" />
                        </NamedLink>
                    </div>

                    <H2 className={css.sectionTitle}>
                        Further Reading
                    </H2>
                    <p className={css.info}>
                        Further reading on our Trust &amp; Safety protocols:
                    </p>
                    <ul className={css.list}>
                        <li>
                            <a href="https://blog.housesitmatch.com/blog/best-house-sitting-camera-policy/" target="_blank" rel="noopener noreferrer">
                                House Sitting Camera &amp; Smart Home Safety Policy
                            </a>
                        </li>
                        <li>
                            <a href="https://blog.housesitmatch.com/blog/house-sitting-trust-verification/" target="_blank" rel="noopener noreferrer">
                                How We Vet Members: Is It Safe? Trust &amp; ID Verification for House Sitters
                            </a>
                        </li>
                        <li>
                            <a href="https://blog.housesitmatch.com/blog/the-ultimate-housesitting-safety-guide/" target="_blank" rel="noopener noreferrer">
                                The Ultimate Housesitting Safety Guide
                            </a>
                        </li>
                        <li>
                            <a href="https://blog.housesitmatch.com/blog/housesit-match-vets-24-7-pet-safety/" target="_blank" rel="noopener noreferrer">
                                Pet Emergency Support for House Sitters and Homeowners
                            </a>
                        </li>
                    </ul>

                    <p className={css.closingQuote}>
                        Trust isn’t something we ask you to give us. It’s something we work every day to earn.
                    </p>

                    <H2 className={css.sectionTitle}>
                        FAQs
                    </H2>
                    <CollapseFAQ
                        list={faqs}
                    />
                </div>
            </LayoutSingleColumn>
        </Page>
    );
};

export default TrustAndSafetyPage;
